import { fetchWithAuth } from "./authHelpers";
import { useState } from 'react';

const cache = {};
const userInventoryCache = {}; // Separate cache for user inventories by userNumber

export const useInventory = () => {
    const [inventorySize, setInventorySize] = useState(0);
    const fetchInventoryCards = async (filters, sortBy, sortOrder, allowedPlayers, skipCache = false) => {

        // Get the current user ID
        let userID;
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/users/current`, 'GET');
            const userData = await response.json();
            if (!response.ok) {
                throw new Error(userData.message || 'Failed to fetch user data');
            }
            userID = userData._id;
        } catch (error) {
            console.error('Error fetching user ID');
            return null;
        }

        const cacheKey = `${userID}`;

        // Add skipCache parameter to bypass the cache when needed
        if (!skipCache && cache[cacheKey]) {
            return cache[cacheKey];
        }

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/inventory/getCards/players`, 'POST');
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            cache[cacheKey] = data; // Cache the response
            return data;
        } catch (error) {
            console.error('Error fetching inventory details');
        }
    };

    // Add a function to clear the cache
    const clearInventoryCache = () => {
        Object.keys(cache).forEach(key => {
            delete cache[key];
        });
        Object.keys(userInventoryCache).forEach(key => {
            delete userInventoryCache[key];
        });
        console.log("Inventory cache cleared");
    };
    const addCardToInventory = async (cardID) => {
        if (!cardID) {
            console.error('Invalid data');
            return;
        }

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/inventory/addCardToCurrentUser`, 'POST', { cardID });
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error adding card to inventory');
        }
    }

    const fetchInventorySizeByUserNumber = async (userNumber) => {
        if (!userNumber) {
            console.error('User number is required');
            return null;
        }

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/inventory/size/${userNumber}`, 'GET');
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            setInventorySize(data.size);
        } catch (error) {
            console.error('Error fetching inventory size');
            setInventorySize(0);
            return null;
        }
    }

    const fetchInventoryByUserNumber = async (userNumber, skipCache = false) => {
        if (!userNumber) {
            console.error('User number is required');
            return null;
        }

        const cacheKey = `user_${userNumber}`;

        // Check cache first unless skipCache is true
        if (!skipCache && userInventoryCache[cacheKey]) {
            return userInventoryCache[cacheKey];
        }

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/inventory/userNumber/${userNumber}`, 'GET');
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            // Cache the response
            userInventoryCache[cacheKey] = data;
            return data;
        } catch (error) {
            console.error('Error fetching inventory by user number');
            return null;
        }
    }

    return { fetchInventoryCards, addCardToInventory, fetchInventorySizeByUserNumber, fetchInventoryByUserNumber, inventorySize, clearInventoryCache };
};